import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Mail, Phone, MapPin, Clock, MessageCircle, HeadphonesIcon, CheckCircle, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export function ContactPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.subject || !formData.message) {
      setError('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          subject: formData.subject,
          message: formData.message
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      setSubmitSuccess(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (err) {
      setError('Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="contact-page">
      <Header />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-[#E8F9EE] to-white py-12 sm:py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Get in Touch</h1>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600">
            Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>
      </div>

      <div className="flex-1 py-12 sm:py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-12 sm:mb-16">
            {/* Contact Cards */}
            <Card className="p-5 sm:p-6 border border-gray-200 hover:shadow-lg transition-shadow text-center bg-white">
              <div className="w-12 h-12 bg-[#E8F9EE] rounded-lg flex items-center justify-center mx-auto mb-4">
                <Mail className="w-6 h-6 text-[#00D95F]" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Email Us</h3>
              <p className="text-gray-600 mb-2 text-sm">Our team is here to help</p>
              <a href="mailto:info@6miles.io" className="text-[#00D95F] hover:underline text-sm">
                info@6miles.io
              </a>
            </Card>

            <Card className="p-5 sm:p-6 border border-gray-200 hover:shadow-lg transition-shadow text-center bg-white">
              <div className="w-12 h-12 bg-[#E8F9EE] rounded-lg flex items-center justify-center mx-auto mb-4">
                <Phone className="w-6 h-6 text-[#00D95F]" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Call Us</h3>
              <p className="text-gray-600 mb-2 text-sm">Mon-Fri from 9am to 6pm IST</p>
              <a href="tel:+919876543210" className="text-[#00D95F] hover:underline text-sm">
                +91 98765 43210
              </a>
            </Card>

            <Card className="p-5 sm:p-6 border border-gray-200 hover:shadow-lg transition-shadow text-center bg-white">
              <div className="w-12 h-12 bg-[#E8F9EE] rounded-lg flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-6 h-6 text-[#00D95F]" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">WhatsApp</h3>
              <p className="text-gray-600 mb-2 text-sm">Chat with our support team</p>
              <a href="https://wa.me/919876543210" target="_blank" rel="noopener noreferrer" className="text-[#00D95F] hover:underline text-sm">
                Start a conversation
              </a>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Send us a message</h2>
              <p className="text-gray-600 mb-6 sm:mb-8 text-sm sm:text-base">
                Fill out the form below and we'll get back to you within 24 hours.
              </p>

              {submitSuccess ? (
                <Card className="p-8 border border-[#00D95F] bg-[#E8F9EE] text-center">
                  <div className="w-16 h-16 bg-[#00D95F] rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Message Sent!</h3>
                  <p className="text-gray-600 mb-4">
                    Thank you for contacting us. We'll get back to you within 24 hours.
                  </p>
                  <Button 
                    onClick={() => setSubmitSuccess(false)}
                    variant="outline"
                    className="border-[#00D95F] text-[#00D95F]"
                  >
                    Send Another Message
                  </Button>
                </Card>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                  {error && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                      {error}
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="firstName" className="text-gray-900">First Name</Label>
                      <Input
                        id="firstName"
                        type="text"
                        placeholder="John"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className="w-full"
                        data-testid="contact-firstname-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName" className="text-gray-900">Last Name</Label>
                      <Input
                        id="lastName"
                        type="text"
                        placeholder="Doe"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className="w-full"
                        data-testid="contact-lastname-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-gray-900">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full"
                      data-testid="contact-email-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject" className="text-gray-900">Subject</Label>
                    <Input
                      id="subject"
                      type="text"
                      placeholder="How can we help?"
                      value={formData.subject}
                      onChange={handleInputChange}
                      className="w-full"
                      data-testid="contact-subject-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-gray-900">Message</Label>
                    <Textarea
                      id="message"
                      placeholder="Tell us more about your inquiry..."
                      value={formData.message}
                      onChange={handleInputChange}
                      className="w-full min-h-[120px] sm:min-h-[150px]"
                      data-testid="contact-message-input"
                    />
                  </div>

                  <Button 
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#00D95F] hover:bg-[#00C054] text-white"
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Sending...
                      </>
                    ) : (
                      'Send Message'
                    )}
                  </Button>
                </form>
              )}
            </div>

            {/* Contact Information & Map */}
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 sm:mb-8">Visit our office</h2>
              
              <div className="space-y-5 sm:space-y-6 mb-6 sm:mb-8">
                <div className="flex gap-4">
                  <div className="w-11 h-11 sm:w-12 sm:h-12 bg-[#E8F9EE] rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-[#00D95F]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Address</h3>
                    <p className="text-gray-600 text-sm sm:text-base">
                      A32 Juhu Sun N Sea bldg<br />
                      Juhu Tara Rd, Santacruz West<br />
                      Mumbai 400049, India
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-11 h-11 sm:w-12 sm:h-12 bg-[#E8F9EE] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-[#00D95F]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Office Hours</h3>
                    <p className="text-gray-600 text-sm sm:text-base">
                      Monday - Friday: 9:00 AM - 6:00 PM IST<br />
                      Saturday - Sunday: Closed
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-11 h-11 sm:w-12 sm:h-12 bg-[#E8F9EE] rounded-lg flex items-center justify-center flex-shrink-0">
                    <HeadphonesIcon className="w-5 h-5 sm:w-6 sm:h-6 text-[#00D95F]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Support Hours</h3>
                    <p className="text-gray-600 text-sm sm:text-base">
                      24/7 Support Available<br />
                      via Email & WhatsApp
                    </p>
                  </div>
                </div>
              </div>

              {/* Map Placeholder */}
              <div className="w-full h-48 sm:h-64 bg-gradient-to-br from-[#E8F9EE] to-[#D4F4E0] rounded-lg overflow-hidden flex items-center justify-center border border-[#00D95F]/20">
                <div className="text-center">
                  <MapPin className="w-12 h-12 text-[#00D95F] mx-auto mb-2" />
                  <p className="text-gray-600 text-sm">Mumbai, India</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Quick Links */}
      <div className="bg-gray-50 py-12 sm:py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Looking for answers?</h2>
          <p className="text-base sm:text-xl text-gray-600 mb-6 sm:mb-8">
            Check out our FAQ section for quick answers to common questions.
          </p>
          <Button variant="outline" className="border-gray-300" asChild>
            <Link to="/#faq">Visit FAQ</Link>
          </Button>
        </div>
      </div>

      <Footer />
    </div>
  );
}
